/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  FileText,
  Globe2,
  TagIcon,
  Youtube,
  Facebook,
  Instagram,
  Info,
  Link as LinkIcon,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { API_URL, ATTRIBUTE_KEYS, USER_ROLES } from '@/constant/constant';
import { useUser } from '@/contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import BiglocalLoader from '@/components/common/BigLocalLoader';
import { getAttributeLabel } from '@/utils/VendorAttributesUtils';
import axiosInstance from '../../../utils/axiosInstance';

// A reusable input component with a leading icon
const InputWithIcon = ({ id, label, value, onChange, icon: Icon, error, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-semibold text-slate-800">
      {label}
    </Label>
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        className={`h-11 pl-10 focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1 ${
          error ? 'border-red-500' : ''
        }`}
        {...props}
      />
    </div>
    {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

// --- MAIN FORM COMPONENT ---
const VendorShopSettings = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State to hold ALL attributes for the vendor (used for submission)
  const [allVendorAttributes, setAllVendorAttributes] = useState([]);

  // State to manage ONLY the form fields on this page
  const [formFields, setFormFields] = useState({
    [ATTRIBUTE_KEYS.YOUTUBE]: '',
    [ATTRIBUTE_KEYS.FACEBOOK]: '',
    [ATTRIBUTE_KEYS.INSTAGRAM]: '',
    [ATTRIBUTE_KEYS.TWITTER]: '',
    [ATTRIBUTE_KEYS.WEBSITE]: '',
    [ATTRIBUTE_KEYS.TAGLINE]: '',
    [ATTRIBUTE_KEYS.ABOUT_US]: '',
  });

  // Effect to fetch initial data when the user context is available
  useEffect(() => {
    const fetchAndSetAttributes = async () => {
      if (!user) return;

      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/attribute`,
          {
            headers: {
              sessionToken: localStorage.getItem('sessionToken'),
              'X-USER-ROLE': USER_ROLES.VENDOR,
            },
          }
        );

        const masterAttributes = response.data || [];
        const vendorValues = user.attributeValues || [];

        // Merge master list with existing vendor values
        const mergedAttributes = masterAttributes.map((masterAttr) => {
          const vendorAttr = vendorValues.find((vAttr) => vAttr.name === masterAttr.name);
          return {
            ...masterAttr,
            value: vendorAttr?.value ?? '', // Use vendor value if present, else default
            id: vendorAttr?.id || masterAttr.id,
          };
        });

        // Store the complete list of attributes for submission later
        setAllVendorAttributes(mergedAttributes);

        // Populate the local form state with values from the merged list
        const initialFormFields = {};
        Object.keys(formFields).forEach((key) => {
          const attribute = mergedAttributes.find((attr) => attr.name === key);
          initialFormFields[key] = attribute?.value ?? '';
        });
        setFormFields(initialFormFields);
      } catch (error) {
        console.error('Failed to fetch attributes:', error);
        toast.error('Could not load your shop settings. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetAttributes();
  }, [user]); // Reruns when user data changes

  // Handler to update the local form state
  const handleInputChange = (name, value) => {
    setFormFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submission handler
  const handleSubmit = async () => {
    if (!user) {
      toast.error('User information is not available.');
      return;
    }
    setIsSubmitting(true);
    try {
      const updatedAttributes = allVendorAttributes.map((attr) => {
        if (Object.hasOwn(formFields, attr.name)) {
          return { ...attr, value: formFields[attr.name] };
        }
        return attr;
      });

      // The API expects a clean payload of {id, name, value}
      const finalPayload = updatedAttributes.map(({ id, name, value }) => ({
        id,
        name,
        value,
      }));

      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/attribute`,
        finalPayload,
        {
          headers: {
            'X-Vendor-Id': user.id,
            sessionToken: localStorage.getItem('sessionToken'),
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        }
      );

      toast.success('Shop settings updated successfully!');
      const contactNo = localStorage.getItem('contactNo');
      await refreshUser(contactNo, true); // Refresh user context to reflect changes
      navigate(-1); // Go back to the previous page
    } catch (error) {
      console.error('Error updating vendor attributes:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <BiglocalLoader title="Loading Your Shop Settings..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Shop Profile</h1>
              <p className="text-sm text-slate-500">Manage your public business information.</p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-36 bg-green-600 text-sm hover:bg-green-700"
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </header>

        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Business Details & Social Presence</CardTitle>
            <CardDescription>
              This information will be displayed on your public shop page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* --- Business Information Section --- */}
            <section className="space-y-6">
              <h3 className="flex items-center text-lg font-semibold text-slate-800">
                <Info className="mr-2 h-5 w-5 text-green-600" />
                Business Information
              </h3>
              <InputWithIcon
                id={ATTRIBUTE_KEYS.TAGLINE}
                label={getAttributeLabel(ATTRIBUTE_KEYS.TAGLINE)}
                value={formFields[ATTRIBUTE_KEYS.TAGLINE]}
                onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.TAGLINE, e.target.value)}
                type="text"
                placeholder="e.g., Freshly Baked Goods Since 1998"
                icon={TagIcon}
              />
              <div className="space-y-2">
                <Label htmlFor={ATTRIBUTE_KEYS.ABOUT_US} className="font-semibold text-slate-800">
                  {getAttributeLabel(ATTRIBUTE_KEYS.ABOUT_US)}
                </Label>
                <div className="rounded-md border border-slate-200 bg-white">
                  <ReactQuill
                    theme="snow"
                    value={formFields[ATTRIBUTE_KEYS.ABOUT_US]}
                    onChange={(value) => handleInputChange(ATTRIBUTE_KEYS.ABOUT_US, value)}
                    placeholder="Tell your customers about your business..."
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'list',
                      'bullet',
                      'align',
                      'link',
                    ]}
                    className="rich-text-editor"
                  />
                </div>
              </div>
            </section>

            {/* --- Social Media Section --- */}
            <section className="space-y-6">
              <h3 className="flex items-center text-lg font-semibold text-slate-800">
                <LinkIcon className="mr-2 h-5 w-5 text-green-600" />
                Social Media & Website
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <InputWithIcon
                  id={ATTRIBUTE_KEYS.WEBSITE}
                  label={getAttributeLabel(ATTRIBUTE_KEYS.WEBSITE)}
                  value={formFields[ATTRIBUTE_KEYS.WEBSITE]}
                  onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.WEBSITE, e.target.value)}
                  type="url"
                  placeholder="https://your-website.com"
                  icon={Globe2}
                />
                <InputWithIcon
                  id={ATTRIBUTE_KEYS.YOUTUBE}
                  label={getAttributeLabel(ATTRIBUTE_KEYS.YOUTUBE)}
                  value={formFields[ATTRIBUTE_KEYS.YOUTUBE]}
                  onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.YOUTUBE, e.target.value)}
                  type="url"
                  placeholder="https://youtube.com/your-channel"
                  icon={Youtube}
                />
                <InputWithIcon
                  id={ATTRIBUTE_KEYS.FACEBOOK}
                  label={getAttributeLabel(ATTRIBUTE_KEYS.FACEBOOK)}
                  value={formFields[ATTRIBUTE_KEYS.FACEBOOK]}
                  onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.FACEBOOK, e.target.value)}
                  type="url"
                  placeholder="https://facebook.com/your-page"
                  icon={Facebook}
                />
                <InputWithIcon
                  id={ATTRIBUTE_KEYS.INSTAGRAM}
                  label={getAttributeLabel(ATTRIBUTE_KEYS.INSTAGRAM)}
                  value={formFields[ATTRIBUTE_KEYS.INSTAGRAM]}
                  onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.INSTAGRAM, e.target.value)}
                  type="url"
                  placeholder="https://instagram.com/your-profile"
                  icon={Instagram}
                />
                <InputWithIcon
                  id={ATTRIBUTE_KEYS.TWITTER}
                  label={getAttributeLabel(ATTRIBUTE_KEYS.TWITTER)}
                  value={formFields[ATTRIBUTE_KEYS.TWITTER]}
                  onChange={(e) => handleInputChange(ATTRIBUTE_KEYS.TWITTER, e.target.value)}
                  type="text"
                  placeholder="Twitter/X link"
                  icon={X}
                />
              </div>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VendorShopSettings;
